import { useEffect, useMemo } from "react";
import { OrderApi, useGetOrdersQuery } from "@/redux/services/order/order.api";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import { useMercureSubscription } from "@/lib/hooks";
import { useAppDispatch } from "@/redux/features/hooks";
import { normalizeOrder } from "@/redux/services/order/normalizeOrder";
import { endOfDay, startOfDay } from "date-fns";

// Statuses worth surfacing on the floor hub — everything except the
// cancelled/rejected/draft tail, which staff don't need to act on.
const SHIFT_STATUSES: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
  "picked_up",
  "delivered",
];

const IN_KITCHEN: OrderStatus[] = ["pending", "accepted", "preparing"];

export interface ShiftOrders {
  /** Today's non-cancelled orders, most recent first. */
  orders: Order[];
  /** Total orders today (may exceed the loaded page). */
  ordersToday: number;
  /** Awaiting payment, longest-waiting first. */
  unpaid: Order[];
  /** Ready to serve / pick up, longest-waiting first. */
  ready: Order[];
  /** Still in the kitchen (pending/accepted/preparing), longest-waiting first. */
  active: Order[];
  /** Being prepared (status preparing only), longest-waiting first. */
  preparing: Order[];
  /** Cash collected today (sum of paid amounts). */
  collected: number;
  isLoading: boolean;
}

// One page is plenty for a floor shift; the action lists below only ever care
// about the small active/unpaid subset, so truncation past this is harmless.
const PAGE_SIZE = 100;

/**
 * Fetches today's orders for the restaurant and keeps them live via Mercure,
 * exposing the derived counts and action lists the hub renders. Reuses the
 * same cache key + patch pattern as PosActiveOrders so there's a single source
 * of truth and no refetch round-trips on every event.
 */
export function useShiftOrders(restaurantId: string | undefined): ShiftOrders {
  const dispatch = useAppDispatch();

  const todayRange = useMemo(() => {
    const now = new Date();
    return {
      after: startOfDay(now).toISOString(),
      before: endOfDay(now).toISOString(),
    };
  }, []);

  const queryArgs = useMemo(
    () => ({
      restaurantId: restaurantId ?? "",
      query: {
        status: SHIFT_STATUSES.join(","),
        limit: PAGE_SIZE,
        "order[createdAt]": "desc" as const,
        "createdAt[after]": todayRange.after,
        "createdAt[before]": todayRange.before,
      } as Record<string, unknown>,
    }),
    [restaurantId, todayRange]
  );

  const { data, isLoading } = useGetOrdersQuery(queryArgs, {
    skip: !restaurantId,
  });

  // Patch the cache in place as events arrive: upsert orders that belong to the
  // tracked set, drop ones that leave it (e.g. just cancelled).
  const orderEvent = useMercureSubscription<Order>(
    restaurantId ? `/api/restaurants/${restaurantId}/orders` : null
  );

  useEffect(() => {
    if (!orderEvent) return;
    const incoming = normalizeOrder(orderEvent.data);
    if (!incoming?.id) return;
    const tracked = SHIFT_STATUSES.includes(incoming.status);

    dispatch(
      OrderApi.util.updateQueryData("getOrders", queryArgs, (draft) => {
        const idx = draft.results.findIndex((o) => o.id === incoming.id);
        if (tracked) {
          if (idx !== -1) {
            draft.results[idx] = incoming;
          } else {
            draft.results.unshift(incoming);
            draft.count = (draft.count ?? 0) + 1;
          }
        } else if (idx !== -1) {
          draft.results.splice(idx, 1);
          draft.count = Math.max(0, (draft.count ?? 0) - 1);
        }
      })
    );
  }, [orderEvent, dispatch, queryArgs]);

  const orders = useMemo(() => data?.results ?? [], [data]);

  return useMemo(() => {
    const byOldest = (a: Order, b: Order) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

    return {
      orders,
      ordersToday: data?.count ?? orders.length,
      unpaid: orders.filter((o) => !o.isPaid).sort(byOldest),
      ready: orders.filter((o) => o.status === "ready").sort(byOldest),
      active: orders
        .filter((o) => IN_KITCHEN.includes(o.status))
        .sort(byOldest),
      preparing: orders
        .filter((o) => o.status === "preparing")
        .sort(byOldest),
      collected: orders.reduce((sum, o) => sum + (o.paidAmount ?? 0), 0),
      isLoading,
    };
  }, [orders, data, isLoading]);
}
